export class Game {
  constructor(width, height) {
    this.width = width;
    this.height = height;
    this.isRunning = false;
    this.playerFrequency = 440; // Starting frequency (A4)
    this.monsters = [];
    this.score = 0;
    this.lives = 3;
    this.wave = 1;
    this.waveTimer = 0;
    this.spawnTimer = 0;
    this.eventListeners = {};

    // Wave configuration
    this.waveConfig = {
      1: { types: ['A'], spawnInterval: 4000, duration: 30000 },
      2: { types: ['A', 'B'], spawnInterval: 3500, duration: 40000 },
      3: { types: ['A', 'B'], spawnInterval: 3000, duration: 40000 },
      4: { types: ['A', 'B', 'C'], spawnInterval: 2500, duration: 45000 },
      5: { types: ['A', 'B', 'C'], spawnInterval: 2000, duration: 45000 },
    };

    // Monster type definitions (narrower range for better audio distinction)
    this.monsterTypes = {
      A: { freqRange: [220, 330], speed: 50, waveform: 'sine', color: '#00a8ff' },      // Low
      B: { freqRange: [330, 440], speed: 75, waveform: 'triangle', color: '#ff6b35' }, // Mid
      C: { freqRange: [440, 550], speed: 100, waveform: 'square', color: '#f72585' }   // High
    };

    this.state = {
      playerFrequency: this.playerFrequency,
      monsters: this.monsters,
      score: this.score,
      lives: this.lives,
      wave: this.wave,
      matchState: null // { monster, accuracy, timer }
    };
  }

  start() {
    console.log('Game.start() called');
    this.isRunning = true;
    this.emit('waveChange', this.wave);
    this.emit('livesChange', this.lives);
    this.emit('scoreChange', this.score);
    console.log('Game running, will spawn monsters');
  }

  stop() {
    this.isRunning = false;
  }

  setPlayerFrequency(frequency) {
    this.playerFrequency = frequency;
    this.state.playerFrequency = frequency;
  }

  update(deltaTime) {
    if (!this.isRunning) return;

    const dt = deltaTime / 1000; // Convert to seconds

    // Update wave timer
    this.waveTimer += deltaTime;
    const config = this.waveConfig[this.wave] || this.waveConfig[5];

    // Check if wave is complete
    if (this.waveTimer >= config.duration && this.monsters.length === 0) {
      this.nextWave();
    }

    // Spawn monsters
    this.spawnTimer += deltaTime;
    if (this.spawnTimer >= config.spawnInterval && this.monsters.length < 5) {
      this.spawnMonster();
      this.spawnTimer = 0;
    }

    // Update monsters
    this.monsters.forEach((monster, index) => {
      monster.x -= monster.speed * dt;

      // Monster reached left edge - lose life
      if (monster.x < -50) {
        this.monsters.splice(index, 1);
        this.loseLife();
      }
    });

    // Check frequency matching
    this.checkFrequencyMatching(deltaTime);

    // Update state
    this.state.monsters = this.monsters;
    this.state.playerFrequency = this.playerFrequency;
  }

  spawnMonster() {
    const config = this.waveConfig[this.wave] || this.waveConfig[5];
    const type = config.types[Math.floor(Math.random() * config.types.length)];
    const typeDef = this.monsterTypes[type];

    const frequency = Math.random() * (typeDef.freqRange[1] - typeDef.freqRange[0]) + typeDef.freqRange[0];

    const monster = {
      id: Date.now() + Math.random(),
      type,
      frequency,
      x: this.width,
      y: this.height / 2,
      speed: typeDef.speed,
      waveform: typeDef.waveform,
      color: typeDef.color,
      health: 2000, // milliseconds to kill
      currentHealth: 2000,
      isLocked: false
    };

    this.monsters.push(monster);
    this.emit('monsterSpawn', monster);
  }

  checkFrequencyMatching(deltaTime) {
    // No monsters - nothing to match
    if (this.monsters.length === 0) {
      this.state.matchState = null;
      return;
    }

    const tolerance = 20; // ±20 Hz (easier matching)

    // Group monsters by type for combination logic
    const typeGroups = {};
    this.monsters.forEach(m => {
      if (!typeGroups[m.type]) typeGroups[m.type] = [];
      typeGroups[m.type].push(m);
    });

    // Find target frequency
    let targetMonsters = [];
    let targetFrequency = 0;

    if (Object.keys(typeGroups).length === 1) {
      // Same type - combine frequencies (average)
      const allMonsters = Object.values(typeGroups)[0];
      targetFrequency = allMonsters.reduce((sum, m) => sum + m.frequency, 0) / allMonsters.length;
      targetMonsters = allMonsters;
    } else {
      // Different types - find closest monster
      const closest = this.monsters.reduce((prev, curr) =>
        curr.x < prev.x ? curr : prev
      );
      targetFrequency = closest.frequency;
      targetMonsters = [closest];
    }

    // Check if player frequency matches
    const diff = Math.abs(this.playerFrequency - targetFrequency);
    const accuracy = Math.max(0, 1 - (diff / tolerance));

    if (diff <= tolerance) {
      // Frequency matched!
      targetMonsters.forEach(monster => {
        monster.isLocked = true;
        monster.currentHealth -= deltaTime;

        if (monster.currentHealth <= 0) {
          // Monster killed!
          this.killMonster(monster);
        }
      });

      this.state.matchState = {
        monsters: targetMonsters,
        accuracy,
        targetFrequency
      };
    } else {
      // Not matched - reset lock
      targetMonsters.forEach(monster => {
        monster.isLocked = false;
        monster.currentHealth = monster.health;
      });

      this.state.matchState = null;
    }
  }

  killMonster(monster) {
    const index = this.monsters.indexOf(monster);
    if (index > -1) {
      this.monsters.splice(index, 1);
      this.addScore(100);
      this.emit('monsterKill', monster);
    }
  }

  addScore(points) {
    this.score += points;
    this.state.score = this.score;
    this.emit('scoreChange', this.score);
  }

  loseLife() {
    this.lives--;
    this.state.lives = this.lives;
    this.emit('livesChange', this.lives);

    if (this.lives <= 0) {
      this.gameOver();
    }
  }

  nextWave() {
    this.wave++;
    this.waveTimer = 0;
    this.state.wave = this.wave;
    this.emit('waveChange', this.wave);
  }

  gameOver() {
    this.isRunning = false;
    this.emit('gameOver', this.score);
  }

  // Event system
  on(event, callback) {
    if (!this.eventListeners[event]) {
      this.eventListeners[event] = [];
    }
    this.eventListeners[event].push(callback);
  }

  emit(event, data) {
    if (this.eventListeners[event]) {
      this.eventListeners[event].forEach(callback => callback(data));
    }
  }
}
